import { INTRO_TIMELINE as T, SHOWER, showerPetalCount } from "../../lib/intro";

const EASE_OUT = "cubic-bezier(.22, 1, .36, 1)";
const EASE_EXPO = "cubic-bezier(.16, 1, .3, 1)";
const EASE_DRAW = "cubic-bezier(.65, 0, .35, 1)";
const FLAME_OUT_MS = 280;
const LIGHT_OUT_MS = 380;
const LINE_IN_MS = 600;
const LINE_OUT_MS = 300;
const SKIP_FADE_MS = 300;
const FALL_VH = 112; // from just above the top edge to below the bottom one
const DRAW_MARGIN = 1; // user units, more than half a stroke: keeps a hidden stroke's round cap off the path

/**
 * Prepares `shape` to be drawn along its length and returns the dash offset that hides it completely. The gap is
 * longer than the dash, so no zero-length dash (a round-cap dot) sits at either end while it is hidden.
 */
function prepareStroke(shape) {
  const length = shape.getTotalLength();
  shape.style.strokeDasharray = `${length} ${length + 2 * DRAW_MARGIN}`;
  return length + DRAW_MARGIN;
}

/** The candle is lit and later blown out; the light dies with it and smoke curls up from the wick. */
function lightAndBlowOutCandle(q, animate) {
  const flameTotal = T.candleOut + FLAME_OUT_MS - T.candleOn;
  const flameAt = (ms) => (ms - T.candleOn) / flameTotal;
  animate(q(".intro-flame"), [
    { transform: "scale(0, 0) skewX(0deg)", opacity: 0, offset: 0, easing: EASE_OUT },
    { transform: "scale(1.25, 1.25) skewX(0deg)", opacity: 1, offset: flameAt(T.candleOn + 250) },
    { transform: "scale(1, 1) skewX(0deg)", opacity: 1, offset: flameAt(T.candleOn + 450) },
    { transform: "scale(1, 1) skewX(0deg)", opacity: 1, offset: flameAt(T.candleOut) },
    { transform: "scale(1.1, 1.3) skewX(-10deg)", opacity: 1, offset: flameAt(T.candleOut + FLAME_OUT_MS * 0.35) },
    { transform: "scale(.3, .12) skewX(14deg)", opacity: 0, offset: 1 },
  ], { delay: T.candleOn, duration: flameTotal, easing: "linear" });
  const lightTotal = T.candleOut + LIGHT_OUT_MS - T.candleOn;
  animate(q(".intro-light"), [
    { opacity: 0, transform: "translate(-50%, -50%) scale(.4)", offset: 0, easing: EASE_EXPO },
    { opacity: 1, transform: "translate(-50%, -50%) scale(1)", offset: 1000 / lightTotal },
    { opacity: 1, offset: (T.candleOut - T.candleOn) / lightTotal },
    { opacity: 0, transform: "translate(-50%, -50%) scale(1)", offset: 1 },
  ], { delay: T.candleOn, duration: lightTotal, easing: "linear" });
  const candle = q(".intro-candle");
  animate(candle, [{ opacity: 0 }, { opacity: 1 }], { delay: T.candleOn + 100, duration: 500 });
  animate(candle, [{ filter: "brightness(1)" }, { filter: "brightness(.35)" }], { delay: T.candleOut, duration: 450 });
  animate(q(".intro-ember"), [{ opacity: 0 }, { opacity: 1, offset: 0.15 }, { opacity: 0 }],
    { delay: T.candleOut + FLAME_OUT_MS * 0.6, duration: 800, easing: "linear" });
  curlSmoke(q(".intro-smoke"), animate, T.candleOut + FLAME_OUT_MS * 0.5);
}

/** Two strands of smoke travel up from the wick and fade. */
function curlSmoke(smoke, animate, start) {
  smoke.querySelectorAll("path").forEach((strand, i) => {
    const hidden = prepareStroke(strand);
    animate(strand, [{ strokeDashoffset: hidden }, { strokeDashoffset: 0, offset: 0.55 }, { strokeDashoffset: -hidden }],
      { delay: start + i * 130, duration: 1200, easing: "cubic-bezier(.3, 0, .5, 1)" });
  });
  animate(smoke, [{ transform: "translateY(0cqh)", opacity: 1 }, { opacity: 1, offset: 0.5 }, { transform: "translateY(-6.8cqh)", opacity: 0 }],
    { delay: start, duration: 1400, easing: "ease-out" });
}

/** The invocation lines appear one after another and fade out when the candle dies. */
function whisperLines(lines, animate) {
  T.lines.forEach((start, i) => {
    const total = T.candleOut + LINE_OUT_MS - start;
    const fadeIn = Math.min(LINE_IN_MS, T.candleOut - start);
    animate(lines[i], [
      { opacity: 0, transform: "translateY(2.3cqh)", filter: "blur(.7cqh)", offset: 0, easing: EASE_OUT },
      { opacity: 1, transform: "none", filter: "blur(0px)", offset: fadeIn / total },
      { opacity: 1, offset: (T.candleOut - start) / total },
      { opacity: 0, transform: "none", filter: "blur(0px)", offset: 1 },
    ], { delay: start, duration: total, easing: "linear" });
  });
}

/** The smoke draws her skull, its eyes light up, it breathes once and dissolves while the page is revealed. */
function drawSkull(skull, animate) {
  skull.querySelectorAll(".draw").forEach((shape, i) => {
    animate(shape, [{ strokeDashoffset: prepareStroke(shape) }, { strokeDashoffset: 0 }],
      { delay: T.skullDraw + i * 80, duration: 900, easing: EASE_DRAW });
  });
  skull.querySelectorAll(".lit").forEach((shape) =>
    animate(shape, [{ fillOpacity: 0 }, { fillOpacity: 0.95 }], { delay: T.eyesOpen, duration: 350, easing: "ease-out" }));
  animate(skull.querySelector(".pupils"), [{ opacity: 0 }, { opacity: 1 }], { delay: T.eyesOpen + 100, duration: 250 });
  const glow = (radius, alpha, blur) => `drop-shadow(0 0 ${radius}cqh rgba(249, 235, 212, ${alpha})) blur(${blur}cqh)`;
  animate(skull, [
    { opacity: 1, filter: glow(1.4, 0.45, 0), transform: "translateX(-50%) scale(1)", offset: 0, easing: "ease-in-out" },
    { filter: glow(3.6, 0.75, 0), transform: "translateX(-50%) scale(1.04)", offset: 0.35, easing: "ease-in-out" },
    { opacity: 1, filter: glow(2, 0.5, 0), transform: "translateX(-50%) scale(1.02)", offset: 0.58, easing: "ease-in" },
    { opacity: 0, filter: glow(2, 0.5, 1.1), transform: "translateX(-50%) scale(1.08)", offset: 1 },
  ], { delay: T.skullBreath, duration: 1200, easing: "linear" });
}

/** A fluttering fall: three swings, a spin and a flip, fading in at the top and out at the bottom. */
function fallKeyframes() {
  const direction = () => (Math.random() < 0.5 ? -1 : 1);
  const sway = (1.5 + Math.random() * 2.5) * direction();
  const turn = Math.random() * 360;
  const spin = (200 + Math.random() * 300) * direction();
  const pose = (progress, x) => `translate(${x.toFixed(2)}vw, ${(progress * FALL_VH).toFixed(1)}vh) `
    + `rotate(${(turn + spin * progress).toFixed(0)}deg) rotateY(${(progress * 540).toFixed(0)}deg)`;
  return [
    { offset: 0, transform: pose(0, 0), opacity: 0, easing: "ease-in-out" },
    { offset: 0.06, opacity: 0.95 },
    { offset: 0.33, transform: pose(0.33, sway), easing: "ease-in-out" },
    { offset: 0.66, transform: pose(0.66, -sway), easing: "ease-in-out" },
    { offset: 0.9, opacity: 0.9 },
    { offset: 1, transform: pose(1, sway * 0.5), opacity: 0 },
  ];
}

/** Marigold petals and a few sparks are released above the top edge and flutter down over the page. */
function pourPetals(layer, animate) {
  const width = window.innerWidth;
  const size = Math.min(20, Math.max(11, width * 0.012));
  const count = showerPetalCount(width);
  for (let i = 0; i < count; i += 1) {
    const petal = document.createElement("i");
    const isSpark = i % 6 === 5;
    const scale = 0.8 + Math.random() * 0.8;
    petal.className = isSpark ? "intro-spark" : "intro-petal";
    petal.style.left = `${(Math.random() * 100).toFixed(1)}%`;
    if (!isSpark) {
      petal.style.width = `${(size * scale).toFixed(1)}px`;
      petal.style.height = `${(size * scale * 0.46).toFixed(1)}px`;
    }
    layer.append(petal);
    animate(petal, fallKeyframes(), {
      delay: T.petals + Math.random() * SHOWER.maxDelay,
      duration: SHOWER.minDuration + Math.random() * SHOWER.durationSpread,
      easing: "linear",
    });
  }
}

/**
 * Plays the intro inside `root` (the IntroOverlay markup). `onEnter`: the hero may start its entrance; `onReveal`:
 * the page is fully uncovered; `onDone`: the entrance is over (also called on skip); `onFinish`: the overlay can be
 * removed.
 * @returns {{ skip: () => void, destroy: () => void }}
 */
export function playIntro(root, { onEnter, onReveal, onDone, onFinish }) {
  const animations = [];
  const timers = [];
  let isRevealed = false;
  let isOver = false;
  const animate = (element, keyframes, options) => {
    const animation = element.animate(keyframes, { easing: EASE_OUT, fill: "both", ...options });
    animations.push(animation);
    return animation;
  };
  const q = (selector) => root.querySelector(selector);
  const at = (ms, callback) => timers.push(setTimeout(callback, ms));

  lightAndBlowOutCandle(q, animate);
  whisperLines(root.querySelectorAll(".intro-lines p"), animate);
  drawSkull(q(".intro-skull"), animate);
  pourPetals(q(".intro-petals"), animate);
  animate(q(".intro-curtain"), [{ "--intro-reveal": "0%" }, { "--intro-reveal": "130%" }],
    { delay: T.reveal, duration: T.revealEnd - T.reveal, easing: "cubic-bezier(.45, 0, .4, 1)" });
  animate(q(".intro-skip"), [{ opacity: 1 }, { opacity: 0 }], { delay: T.reveal, duration: 300, easing: "ease-out" });

  at(T.enter, onEnter);
  at(T.revealEnd, () => {
    isRevealed = true;
    root.classList.add("is-revealed");
    onReveal();
  });
  at(T.done, onDone);
  at(T.finish, () => {
    isOver = true;
    onFinish();
  });

  return {
    skip() {
      if (isRevealed || isOver) return;
      isOver = true;
      timers.forEach(clearTimeout);
      onDone();
      animate(root, [{ opacity: 1 }, { opacity: 0 }], { duration: SKIP_FADE_MS, easing: "ease-out", fill: "forwards" })
        .finished.then(onFinish, () => {});
    },
    destroy() {
      timers.forEach(clearTimeout);
      animations.forEach((animation) => animation.cancel());
      q(".intro-petals").textContent = "";
      root.classList.remove("is-playing", "is-revealed");
    },
  };
}
